import type { ReactNode } from 'react';
import { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';

export type SearchConfig = {
  placeholder?: string;
  onSearch: (term: string) => void;
};

export type FilterConfig = {
  active: boolean;
  onClick: () => void;
  onClear?: () => void;
};

export type ExportConfig = {
  endpoint: string;
  params: Record<string, string | number | undefined>;
  /** Base filename without extension, e.g. "categories". */
  filename: string;
};

export type PrimaryActionConfig = {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
};

export type ModuleHeaderConfig = {
  title: string;
  search?: SearchConfig;
  filter?: FilterConfig;
  export?: ExportConfig;
  primaryAction?: PrimaryActionConfig;
};

/** A layout-level header declaration (the title only), owned by a hub/layout route. */
export type ModuleHeaderBase = {
  title: string;
};

type ContextValue = {
  /** Title-only header from the hub/layout, painted before the lazy page chunk resolves. */
  base: ModuleHeaderBase | null;
  setBase: (base: ModuleHeaderBase | null) => void;
  /** Full header from the active page (title + toolbar). Takes precedence over `base`. */
  page: ModuleHeaderConfig | null;
  setPage: (config: ModuleHeaderConfig | null) => void;
};

const ModuleHeaderContext = createContext<ContextValue | null>(null);

export function ModuleHeaderProvider({ children }: { children: ReactNode }) {
  const [base, setBase] = useState<ModuleHeaderBase | null>(null);
  const [page, setPage] = useState<ModuleHeaderConfig | null>(null);
  return (
    <ModuleHeaderContext.Provider value={{ base, setBase, page, setPage }}>
      {children}
    </ModuleHeaderContext.Provider>
  );
}

/**
 * Read the effective header config (null when none). For the header slot.
 *
 * The active page's full config wins when present; otherwise the hub's
 * title-only `base` is used so the header (and thus the tabs below it) paints
 * in its final position the instant the hub chunk loads — before the lazy page
 * chunk resolves. This prevents the tabs from jumping down when the page's
 * toolbar arrives. The two layers are independent slots, so React's
 * child-before-parent effect ordering can't clobber the page config.
 */
export function useModuleHeaderConfig(): ModuleHeaderConfig | null {
  const ctx = useContext(ModuleHeaderContext);
  if (!ctx) return null;
  return ctx.page ?? (ctx.base ? { title: ctx.base.title } : null);
}

/**
 * Declare the layout-level header title from a hub/layout route. The title
 * paints with the tabs immediately (the hub chunk loads first); the child page
 * then enriches the header with its toolbar via {@link useModuleHeader} without
 * shifting the tabs. Persists across child tab switches since the hub stays
 * mounted.
 */
export function useModuleHeaderBase(base: ModuleHeaderBase): void {
  const ctx = useContext(ModuleHeaderContext);
  if (!ctx) throw new Error('useModuleHeaderBase must be used within <ModuleHeaderProvider>');
  const { setBase } = ctx;

  // Layout effect (not useEffect) so the title is published before the browser
  // paints — the hub's title appears in the same frame as its tabs, never a
  // frame later.
  useLayoutEffect(() => {
    setBase({ title: base.title });
    return () => setBase(null);
  }, [setBase, base.title]);
}

/**
 * Declare this page's header. Handlers are proxied through a ref so changing
 * their identity every render never retriggers the effect — only a change in
 * the primitive signature (title, which pieces exist, filter.active, export
 * params/endpoint/filename, primaryAction.label) republishes the config.
 */
export function useModuleHeader(config: ModuleHeaderConfig): void {
  const ctx = useContext(ModuleHeaderContext);
  if (!ctx) throw new Error('useModuleHeader must be used within <ModuleHeaderProvider>');
  const { setPage } = ctx;

  const ref = useRef(config);
  // Sync the ref after every render so callbacks always close over the latest config.
  useLayoutEffect(() => {
    ref.current = config;
  });

  const signature = JSON.stringify({
    title: config.title,
    search: config.search ? { placeholder: config.search.placeholder ?? null } : null,
    filter: config.filter
      ? { active: config.filter.active, clearable: Boolean(config.filter.onClear) }
      : null,
    export: config.export
      ? {
          endpoint: config.export.endpoint,
          filename: config.export.filename,
          params: config.export.params,
        }
      : null,
    primaryAction: config.primaryAction ? { label: config.primaryAction.label } : null,
  });

  // Layout effect (not useEffect) so the header is published before paint — the
  // page's title+toolbar render in the same frame as its content, so the
  // content never paints first and then shifts down when the header appears.
  useLayoutEffect(() => {
    const c = ref.current;
    setPage({
      title: c.title,
      search: c.search
        ? {
            placeholder: c.search.placeholder,
            onSearch: (t: string) => ref.current.search?.onSearch(t),
          }
        : undefined,
      filter: c.filter
        ? {
            active: c.filter.active,
            onClick: () => ref.current.filter?.onClick(),
            onClear: c.filter.onClear ? () => ref.current.filter?.onClear?.() : undefined,
          }
        : undefined,
      export: c.export,
      primaryAction: c.primaryAction
        ? {
            label: c.primaryAction.label,
            icon: c.primaryAction.icon,
            onClick: () => ref.current.primaryAction?.onClick(),
          }
        : undefined,
    });
    return () => setPage(null);
    // ref-proxied handlers are intentionally excluded; only `signature` drives updates.
  }, [setPage, signature]);
}
